<template>
  <div v-if="!dataLoaded"
    style="position: fixed; width: 100vw; height: 100vh; display: flex; justify-content: center; align-items: center; background-color: #fff; z-index: 999999;">
    <ProgressSpinner />
  </div>
  <div class="app-container">
    <!-- Контейнер -->
    <div class="tg-app-container">
      <!-- Шапка в стиле скетча -->
      <header class="header">
        <div class="header-sketch" v-if="currentPage !== 'matching'">
          <!-- Рукописный заголовок -->
          <div class="handwritten-title">
            <span class="month-badge">ПОТОК</span>
            <h1 class="main-title">Учебная платформа</h1>
            <div class="subtitle-arrow">
              <svg class="arrow-sketch" viewBox="0 0 100 30" fill="none" stroke="currentColor">
                <path d="M10,15 Q30,5 50,15 T90,15" stroke-width="2" fill="none" />
                <path d="M85,10 L90,15 L85,20" stroke-width="2" fill="none" />
              </svg>
              <p class="subtitle-text">Курсы, статьи и видеоуроки для саморазвития</p>
            </div>
          </div>

          <!-- Декоративные элементы -->
          <div class="sketch-elements">
            <div class="brain-sketch">
              <svg viewBox="0 0 50 40" fill="none" stroke="currentColor">
                <path d="M15,20 Q10,10 20,8 Q30,5 35,10 Q45,8 40,18 Q45,25 35,30 Q25,35 15,30 Q5,25 15,20"
                  stroke-width="1.5" />
                <circle cx="20" cy="15" r="1" fill="currentColor" />
                <circle cx="30" cy="18" r="1" fill="currentColor" />
              </svg>
            </div>
            <div class="lightbulb-sketch">
              <svg viewBox="0 0 30 40" fill="none" stroke="currentColor">
                <circle cx="15" cy="15" r="8" stroke-width="1.5" />
                <path d="M15,23 L15,30" stroke-width="2" />
                <path d="M12,30 L18,30" stroke-width="2" />
                <path d="M10,8 Q15,3 20,8" stroke-width="1" />
              </svg>
            </div>
          </div>
        </div>

      </header>

      <!-- Поиск в рукописном стиле -->
      <div v-if="currentPage === 'explore'" class="search-container">
        <div class="search-sketch-wrapper">
          <div class="search-label">Что ищем?</div>
          <div class="search-input-wrapper">
            <input v-model="searchQuery" type="text" placeholder="Поиск материалов..." class="search-input-sketch">
            <div class="search-underline"></div>
          </div>
        </div>
      </div>

      <!-- Разделы в стиле коллажа (только для главной) -->
      <div v-if="currentPage === 'explore'" class="categories-collage">
        <div class="collage-title">
          <span class="handwritten-text">Выберите раздел:</span>
          <svg class="underline-sketch" viewBox="0 0 200 10" fill="none" stroke="currentColor">
            <path d="M5,5 Q50,2 100,5 T195,5" stroke-width="1.5" />
          </svg>
        </div>

        <div class="categories-grid-sketch">
          <div v-for="(category, index) in categories" :key="category.id" @click="selectedCategory = category"
            :class="['category-card-sketch', selectedCategory === category.name ? 'category-active-sketch' : '']"
            :style="{ transform: `rotate(${getRandomRotation(index)}deg)` }">
            <!-- Рамка в стиле скетча -->
            <div class="sketch-frame">
              <svg class="frame-border" viewBox="0 0 200 150" fill="none" stroke="currentColor">
                <path d="M10,10 L190,12 L188,140 L12,138 Z" stroke-width="2" stroke-dasharray="5,3" />
              </svg>
            </div>

            <div class="category-content-sketch">
              <div class="category-icon-sketch">
                <svg class="category-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="category.icon" />
                </svg>
              </div>
              <h3 class="category-name-handwritten">{{ category.name }}</h3>

              <!-- Прогресс в виде рукописной полоски -->
              <div class="progress-sketch">
                <div class="progress-label">{{ getCategoryProgress(category.text) }}% пройдено</div>
                <svg class="progress-line" viewBox="0 0 100 8" fill="none">
                  <path d="M5,4 L95,4" stroke="#ddd" stroke-width="2" />
                  <path :d="`M5,4 L${5 + getCategoryProgress(category.text) * 0.9},4`" stroke="currentColor"
                    stroke-width="3" />
                </svg>
              </div>
            </div>

            <!-- Стикер с номером -->
            <div class="category-sticker">{{category.text === 'all' ? materials.length : materials.filter(material =>
              material.categories.includes(category.text)).length }}</div>
          </div>
        </div>
      </div>

      <!-- Фильтры в стиле заметок -->
      <div v-if="currentPage === 'explore'" class="tags-notes">
        <div class="notes-title">Фильтры:</div>
        <div class="tag-notes-grid">
          <button v-for="tag in tags" :key="tag.id" @click="selectedTag = tag.id"
            :class="['tag-note', selectedTag === tag.id ? 'tag-note-active' : '']">
            <div class="note-corner"></div>
            {{ tag.name }}
          </button>
        </div>
      </div>

      <!-- Главная страница - список материалов в стиле журнала -->
      <div v-if="currentPage === 'explore'" class="materials-magazine">
        <div v-for="(material, index) in filteredMaterials" :key="material.id" class="material-article"
          @click="selectMaterial(material)" :style="{ transform: `rotate(${getRandomRotation(index, 1)}deg)` }">
          <!-- Рамка статьи -->
          <div class="article-frame" ref="articleFrame">
            <svg class="article-border" viewBox="0 0 300 200" fill="none" stroke="currentColor">
              <rect x="5" y="5" width="290" height="190" stroke-width="1.5" stroke-dasharray="8,4" rx="3" />
            </svg>
          </div>

          <div class="article-content" style="height: 210px;">
            <!-- Заголовок статьи -->
            <div class="article-header">
              <div class="article-type-badge" :class="getTypeClass(material.type)">
                {{ material.type }}
              </div>
              <h3 class="article-title"
                v-html="parseMarkdownV2(material.text ? (material.text.split('\n')[0].length <= 75 ? material.text.split('\n')[0] : material.text.slice(0, 72) + '...') : '')">
              </h3>

              <!-- Кнопка избранного в виде скрепки -->
              <button @click.stop="toggleFavorite(material.id)" class="paperclip-favorite"
                :class="material.isFavorite ? 'paperclip-active' : ''">
                <svg viewBox="0 0 20 30" fill="none" stroke="currentColor">
                  <path d="M6,8 L6,20 Q6,24 10,24 Q14,24 14,20 L14,6 Q14,4 12,4 Q10,4 10,6 L10,18" stroke-width="2" />
                </svg>
              </button>
            </div>

            <p class="article-description"
              v-html="parseMarkdownV2(material.post_body.slice(0, 100) + (material.post_body.length > 100 ? '...' : ''))">
            </p>

            <!-- Статистика в рукописном стиле -->
            <div class="article-stats-sketch">
              <div class="stat-sketch">
                <span class="stat-icon">📅</span>
                <span>{{ getDate(material.created_at) }}</span>
              </div>
              <div class="stat-sketch" :style="{ display: material.rating ? 'block' : 'none' }">
                <span class="stat-icon">⭐</span>
                <span>{{ material.rating }}</span>
              </div>

              <!-- Чекбокс выполнения -->
              <button @click.stop="toggleCompleted(material.id)" class="checkbox-sketch"
                :class="material.completed ? 'checkbox-checked' : ''">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor">
                  <rect x="2" y="2" width="16" height="16" stroke-width="2" rx="2" />
                  <path v-if="material.completed" d="M6,10 L9,13 L14,7" stroke-width="2" />
                </svg>
                <span>{{ material.completed ? 'Готово!' : 'Отметить' }}</span>
              </button>
            </div>
          </div>

          <!-- Декоративная стрелка -->
          <div class="article-arrow" v-if="index % 3 === 0">
            <svg viewBox="0 0 50 30" fill="none" stroke="currentColor">
              <path d="M5,15 Q20,5 35,15" stroke-width="1.5" />
              <path d="M30,10 L35,15 L30,20" stroke-width="1.5" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Страница избранного в стиле доски объявлений -->
      <div v-if="currentPage === 'favorites'" class="favorites-board">
        <div class="board-header">
          <h2 class="board-title">Избранное</h2>
          <div class="pushpin"></div>
        </div>

        <div v-if="favoriteMaterials.length === 0" class="empty-board">
          <div class="empty-note">
            <div class="note-tape"></div>
            <p>Пока ничего не добавлено...</p>
            <button @click="currentPage = 'explore'" class="back-button-sketch">
              Перейти к материалам
            </button>
          </div>
        </div>

        <div v-else class="favorites-grid-board">
          <div v-for="(material, index) in sortedFavorites" :key="material.id" class="favorite-note"
            @click="selectMaterial(material)" :style="{ transform: `rotate(${getRandomRotation(index, 2)}deg)` }">
            <div class="note-tape"></div>
            <div class="note-content">
              <div class="note-type" :class="getTypeClass(material.type)">{{ material.type }}</div>
              <h4 class="note-title" v-html="parseMarkdownV2(material.text ? material.text.split('\n')[0] : '')"></h4>
              <p class="note-desc"
                v-html="parseMarkdownV2(material.text ? (material.text.slice(0, 100) + (material.text.length > 100 ? '...' : '')) : '')">
              </p>
              <div class="note-stats">
                <span>{{ getDate(material.created_at) }}</span>
                <span>{{ material.reactions ? material.reactions.join('') : "-" }}</span>
              </div>
            </div>
            <button @click.stop="toggleFavorite(material.id)" class="remove-pin">×</button>
          </div>
        </div>
      </div>

      <!-- Страница прогресса в стиле инфографики -->
      <div v-if="currentPage === 'progress'" class="progress-infographic">
        <div class="infographic-header">
          <h2 class="infographic-title">Мой прогресс</h2>
          <div class="overall-progress-sketch">
            <div class="progress-circle">
              <svg viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="40" fill="none" stroke="#eee" stroke-width="8" />
                <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" stroke-width="8"
                  :stroke-dasharray="`${getOverallProgress() * 2.51} 251`" transform="rotate(-90 50 50)" />
              </svg>
              <div class="progress-text">{{ getOverallProgress() }}%</div>
            </div>
          </div>
        </div>

        <!-- Прогресс по категориям в виде графиков -->
        <div class="categories-progress-sketch">
          <h3 class="section-title-sketch">По разделам:</h3>
          <div class="progress-bars-sketch">
            <div v-for="category in categories.slice(1)" :key="category.id" class="progress-bar-sketch">
              <div class="bar-label">{{ category.name }}</div>
              <div class="bar-container">
                <div class="bar-fill" :style="{ width: getCategoryProgress(category.text) + '%' }"></div>
                <span class="bar-percent">{{ getCategoryProgress(category.text) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Страница мэтчинга -->
      <div v-if="currentPage === 'matching'" class="matching-page">
        <!-- Если профиль не создан -->
        <div v-if="!userProfile.first_name" class="matching-welcome">
          <div class="matching-intro-note">
            <div class="note-tape"></div>
            <div class="matching-intro-content">
              <h3 class="matching-intro-title">Мэтчинг участников</h3>
              <div class="matching-description">
                <p class="matching-text">Находите единомышленников и коллег для совместных проектов!</p>

                <div class="matching-features">
                  <div class="feature-item">
                    <span class="feature-icon">🤝</span>
                    <span class="feature-text">Знакомьтесь с участниками</span>
                  </div>
                  <div class="feature-item">
                    <span class="feature-icon">💼</span>
                    <span class="feature-text">Находите партнеров по бизнесу</span>
                  </div>
                  <div class="feature-item">
                    <span class="feature-icon">🎯</span>
                    <span class="feature-text">Обменивайтесь опытом</span>
                  </div>
                  <div class="feature-item">
                    <span class="feature-icon">🚀</span>
                    <span class="feature-text">Создавайте совместные проекты</span>
                  </div>
                </div>

                <p class="matching-requirement">Для участия в мэтчинге необходимо заполнить анкету</p>
              </div>

              <button @click="currentPage = 'profile'" class="create-profile-for-matching">
                Заполнить анкету
              </button>
            </div>

            <!-- Декоративные элементы -->
            <div class="matching-decorations">
              <svg class="decoration-hearts" viewBox="0 0 60 30" fill="currentColor">
                <path d="M15 10c0-3 2-5 5-5s5 2 5 5c0 3-5 8-5 8s-5-5-5-8z" />
                <path d="M35 15c0-2 1.5-3.5 3.5-3.5s3.5 1.5 3.5 3.5c0 2-3.5 5.5-3.5 5.5s-3.5-3.5-3.5-5.5z" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Если профиль создан - показываем карточки -->
        <div v-else class="matching-cards-container">
          <div class="matching-header">
            <h2 class="matching-title">Знакомства</h2>
            <div class="matching-counter" @click="showLikedProfiles = !showLikedProfiles">
              {{ showLikedProfiles ? 'Все участники' : 'Избранное' }}
            </div>
          </div>

          <div class="search-container">
            <div class="search-sketch-wrapper">
              <div class="search-label">Что ищем?</div>
              <div class="search-input-wrapper">
                <input v-model="searchQueryProfiles" type="text" placeholder="Поиск участников..."
                  class="search-input-sketch">
                <div class="search-underline"></div>
              </div>
            </div>
          </div>

          <!-- Стек карточек -->
          <div class="cards-stack" ref="cardsStack">
            <div v-for="(profile, index) in filteredProfiles" :key="profile.id" :class="['matching-card']"
              @click="openProfileModal(index)">
              <div class="card-content" style="overflow: hidden;">
                <!-- Заголовок карточки -->
                <div class="card-header">
                  <h3 class="card-name">{{ profile.first_name }} {{ profile.last_name }}</h3>
                  <div class="card-city" v-if="profile.city">{{ profile.city }}</div>
                  <button class="card-footer-button" @click.stop="toggleLikeProfile(profile.id)" style="
                      background: none; 
                      border: 1px solid whitesmoke;
                      border-radius: 50%;
                      padding: 0;
                      margin: 0;
                      width: 40px;
                      height: 40px;
                    ">
                    <Heart :color="userProfile.contacts.includes(String(profile.id)) ? 'red' : 'white'"
                      :fill="userProfile.contacts.includes(String(profile.id)) ? 'red' : 'none'" />
                  </button>
                </div>

                <!-- Основная информация -->
                <div class="card-info">
                  <div class="info-row" style="flex-direction: column; gap: 0.5rem;">
                    <div style="display: flex; align-items: center; gap: 0.75rem;">
                      <span class="info-icon">👔</span>
                      <span class="info-text">{{ profile.role }}</span>
                    </div>
                    <div style="display: flex; align-items: center; gap: 0.75rem;">
                      <span class="info-icon">💼</span>
                      <span class="info-text">{{ profile.niche }}</span>
                      <span class="info-icon">🏢</span>
                      <span class="info-text">{{ profile.company_name }}</span>
                    </div>
                  </div>

                  <div class="info-row">
                    <img :src="profile.avatar" alt="avatar" class="info-avatar" style="width: 100%;">
                  </div>
                </div>
                <div class="card-footer">

                </div>
              </div>
            </div>

            <!-- Модальное окно профиля -->
            <div v-if="openedProfileIndex !== null" class="profile-modal-overlay" @click.self="closeProfileModal">
              <div class="card-profile profile-modal">
                <button class="profile-modal-close" @click="closeProfileModal">×</button>
                <div class="card-profile-content">
                  <!-- Заголовок карточки -->
                  <div class="card-header">
                    <h3 class="card-name">{{ profiles[openedProfileIndex].first_name }} {{
                      profiles[openedProfileIndex].last_name }}</h3>
                    <div class="card-age" v-if="profiles[openedProfileIndex].birth_date">{{
                      getAge(profiles[openedProfileIndex].birth_date) }} лет</div>
                  </div>

                  <!-- Основная информация -->
                  <div class="card-info">
                    <div class="info-row" v-if="profiles[openedProfileIndex].city">
                      <span class="info-icon">📍</span>
                      <span class="info-text">{{ profiles[openedProfileIndex].city }}</span>
                    </div>
                    <div class="info-row" v-if="profiles[openedProfileIndex].niche">
                      <span class="info-icon">💼</span>
                      <span class="info-text">{{ profiles[openedProfileIndex].niche }}</span>
                    </div>
                    <div class="info-row" v-if="profiles[openedProfileIndex].company_name">
                      <span class="info-icon">🏢</span>
                      <span class="info-text">{{ profiles[openedProfileIndex].company_name }}</span>
                    </div>
                    <div class="info-row" v-if="profiles[openedProfileIndex].role">
                      <span class="info-icon">👔</span>
                      <span class="info-text">{{ profiles[openedProfileIndex].role }}</span>
                    </div>
                  </div>

                  <!-- Описания -->
                  <div class="card-descriptions">
                    <div class="description-block" v-if="profiles[openedProfileIndex].about_me">
                      <h4 class="description-title">О себе</h4>
                      <p class="description-text">{{ profiles[openedProfileIndex].about_me }}</p>
                    </div>
                    <div class="description-block" v-if="profiles[openedProfileIndex].help_others">
                      <h4 class="description-title">Чем могу помочь</h4>
                      <p class="description-text">{{ profiles[openedProfileIndex].help_others }}</p>
                    </div>
                    <div class="description-block" v-if="profiles[openedProfileIndex].community_request">
                      <h4 class="description-title">Что ищу</h4>
                      <p class="description-text">{{ profiles[openedProfileIndex].community_request }}</p>
                    </div>
                  </div>

                  <!-- Контакты -->
                  <div class="card-contacts">
                    <h4 class="contacts-title">Контакты</h4>
                    <div class="contacts-list">
                      <div class="contact-item" v-if="profiles[openedProfileIndex].telegram">
                        <span class="contact-icon">📱</span>
                        <span class="contact-text">{{ profiles[openedProfileIndex].telegram }}</span>
                      </div>
                      <div class="contact-item" v-if="profiles[openedProfileIndex].instagram">
                        <span class="contact-icon">📷</span>
                        <span class="contact-text">{{ profiles[openedProfileIndex].instagram }}</span>
                      </div>
                      <div class="contact-item"
                        v-if="profiles[openedProfileIndex].phone && profiles[openedProfileIndex].show_phone">
                        <span class="contact-icon">📞</span>
                        <span class="contact-text">{{ profiles[openedProfileIndex].phone }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Страница профиля -->
      <div v-if="currentPage === 'profile'" class="profile-page">
        <!-- Если профиль не создан -->
        <div v-if="!userProfile.first_name && !isCreatingProfile" class="profile-welcome">
          <div class="welcome-note">
            <div class="note-tape"></div>
            <div class="welcome-content">
              <h3 class="welcome-title">Добро пожаловать!</h3>
              <p class="welcome-text">Создайте свой профиль, чтобы другие участники могли узнать о вас больше</p>
              <button @click="startCreatingProfile" class="create-profile-button">
                Создать профиль
              </button>
            </div>

            <!-- Декоративные элементы -->
            <div class="welcome-decorations">
              <svg class="decoration-star" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <svg class="decoration-heart" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Форма создания/редактирования профиля -->
        <div v-if="isCreatingProfile" class="profile-form-container">
          <div class="form-header">
            <h2 class="form-title">{{ userProfile.first_name ? 'Редактировать профиль' : 'Создание профиля' }}</h2>
            <button @click="cancelProfileCreation" class="cancel-button">×</button>
          </div>

          <form @submit.prevent="saveProfile" class="profile-form">
            <!-- Основная информация -->
            <div class="form-section">
              <h3 class="section-title-form">Основная информация</h3>

              <div class="form-field">
                <label class="field-label">Ваше имя *</label>
                <input v-model="profileForm.first_name" type="text" class="field-input" placeholder="Введите ваше имя"
                  required>
              </div>

              <div class="form-field">
                <label class="field-label">Ваша фамилия *</label>
                <input v-model="profileForm.last_name" type="text" class="field-input"
                  placeholder="Введите вашу фамилию" required>
              </div>

              <div class="form-field">
                <label class="field-label">Дата рождения</label>
                <DatePicker v-model="profileForm.birth_date" placeholder="дд.мм.гггг" class="field-input" />
              </div>

              <div class="form-field">
                <label class="field-label">Ваш город</label>
                <select v-model="profileForm.city" class="field-select">
                  <option value="">Выберите город</option>
                  <option v-for="city in cities" :key="city" :value="city">{{ city }}</option>
                </select>
              </div>

              <div class="form-field">
                <label class="field-label">Номер телефона</label>
                <input v-model="profileForm.phone_number" type="tel" class="field-input"
                  placeholder="+7 (999) 123-45-67">
                <div class="checkbox-field">
                  <input v-model="profileForm.show_phone" type="checkbox" id="showPhone" class="field-checkbox">
                  <label for="showPhone" class="checkbox-label">Показывать мой номер другим участникам</label>
                </div>
              </div>
            </div>

            <!-- Социальные сети -->
            <div class="form-section">
              <h3 class="section-title-form">Социальные сети</h3>

              <div class="form-row">
                <div class="form-field">
                  <label class="field-label">Логин Telegram</label>
                  <input v-model="profileForm.telegram" type="text" class="field-input" placeholder="@username">
                </div>

                <div class="form-field">
                  <label class="field-label">Логин Instagram</label>
                  <input v-model="profileForm.instagram" type="text" class="field-input"
                    placeholder="@username (опционально)">
                </div>
              </div>
            </div>

            <!-- Профессиональная информация -->
            <div class="form-section">
              <h3 class="section-title-form">Профессиональная информация</h3>

              <div class="form-row">
                <div class="form-field">
                  <label class="field-label">Ниша</label>
                  <select v-model="profileForm.niche" class="field-select">
                    <option value="">Выберите нишу</option>
                    <option v-for="niche in niches" :key="niche" :value="niche">{{ niche }}</option>
                  </select>
                </div>

                <div class="form-field">
                  <label class="field-label">Роль в компании</label>
                  <select v-model="profileForm.role" class="field-select">
                    <option value="">Выберите роль</option>
                    <option v-for="role in roles" :key="role" :value="role">{{ role }}</option>
                  </select>
                </div>
              </div>

              <div class="form-row">
                <div class="form-field">
                  <label class="field-label">Название компании</label>
                  <input v-model="profileForm.company_name" type="text" class="field-input"
                    placeholder="Введите название компании">
                </div>

                <div class="form-field">
                  <label class="field-label">Ссылка на сайт компании</label>
                  <input v-model="profileForm.website" type="url" class="field-input" placeholder="https://example.com">
                </div>
              </div>
            </div>

            <!-- Дополнительная информация -->
            <div class="form-section">
              <h3 class="section-title-form">Расскажите о себе</h3>

              <div class="form-field">
                <label class="field-label">Пару слов о себе</label>
                <div class="textarea-container">
                  <textarea v-model="profileForm.about_me" class="field-textarea"
                    placeholder="Расскажите немного о себе..." maxlength="350"></textarea>
                  <div class="char-counter">{{ getCharacterCount(profileForm.about_me, 350) }}</div>
                </div>
              </div>

              <div class="form-field">
                <label class="field-label">О вашем бизнесе</label>
                <div class="textarea-container">
                  <textarea v-model="profileForm.about_business" class="field-textarea"
                    placeholder="Расскажите о вашем бизнесе..." maxlength="350"></textarea>
                  <div class="char-counter">{{ getCharacterCount(profileForm.about_business, 350) }}</div>
                </div>
              </div>

              <div class="form-field">
                <label class="field-label">Чем вы можете быть полезны для других участников?</label>
                <div class="textarea-container">
                  <textarea v-model="profileForm.help_others" class="field-textarea"
                    placeholder="Опишите, как вы можете помочь другим..." maxlength="350"></textarea>
                  <div class="char-counter">{{ getCharacterCount(profileForm.help_others, 350) }}</div>
                </div>
              </div>

              <div class="form-field">
                <label class="field-label">Ваш запрос к сообществу</label>
                <div class="textarea-container">
                  <textarea v-model="profileForm.community_request" class="field-textarea"
                    placeholder="Что вы хотели бы получить от сообщества..." maxlength="350"></textarea>
                  <div class="char-counter">{{ getCharacterCount(profileForm.community_request, 350) }}</div>
                </div>
              </div>
            </div>

            <!-- Кнопки -->
            <div class="form-actions">
              <button type="button" @click="cancelProfileCreation" class="cancel-form-button">
                Отмена
              </button>
              <button type="submit" class="save-profile-button">
                Сохранить профиль
              </button>
            </div>
          </form>
        </div>

        <!-- Отображение созданного профиля -->
        <div v-if="userProfile.first_name && !isCreatingProfile" class="profile-display">
          <div class="profile-header">
            <h2 class="profile-name">{{ userProfile.first_name }} {{ userProfile.last_name }}</h2>
            <button @click="editProfile" class="edit-profile-button">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Редактировать
            </button>
          </div>

          <div class="profile-content">
            <!-- Основная информация -->
            <div class="profile-section" v-if="userProfile.birth_date || userProfile.city">
              <h3 class="profile-section-title">Основная информация</h3>
              <div class="profile-info-grid">
                <div v-if="userProfile.birth_date" class="profile-info-item">
                  <span class="info-label">Дата рождения:</span>
                  <span class="info-value">{{ new Date(userProfile.birth_date).toLocaleDateString('ru-RU') }}</span>
                </div>
                <div v-if="userProfile.city" class="profile-info-item">
                  <span class="info-label">Город:</span>
                  <span class="info-value">{{ userProfile.city }}</span>
                </div>
                <div v-if="userProfile.phone && userProfile.show_phone" class="profile-info-item">
                  <span class="info-label">Телефон:</span>
                  <span class="info-value">{{ userProfile.phone }}</span>
                </div>
              </div>
            </div>

            <!-- Социальные сети -->
            <div class="profile-section" v-if="userProfile.telegram || userProfile.instagram">
              <h3 class="profile-section-title">Контакты</h3>
              <div class="profile-contacts">
                <a v-if="userProfile.telegram" :href="`https://t.me/${userProfile.telegram.replace('@', '')}`"
                  class="contact-link telegram">
                  <!--<span class="contact-icon">📱</span>-->
                  <Send />
                  <span>{{ userProfile.telegram }}</span>
                </a>
                <a v-if="userProfile.instagram"
                  :href="`https://instagram.com/${userProfile.instagram.replace('@', '')}`"
                  class="contact-link instagram">
                  <Instagram />
                  <span>{{ userProfile.instagram }}</span>
                </a>
              </div>
            </div>

            <!-- Профессиональная информация -->
            <div class="profile-section"
              v-if="userProfile.niche || userProfile.company_name || userProfile.role || userProfile.website">
              <h3 class="profile-section-title">Профессиональная деятельность</h3>
              <div class="profile-info-grid">
                <div v-if="userProfile.niche" class="profile-info-item">
                  <span class="info-label">Ниша:</span>
                  <span class="info-value">{{ userProfile.niche }}</span>
                </div>
                <div v-if="userProfile.company_name" class="profile-info-item">
                  <span class="info-label">Компания:</span>
                  <span class="info-value">{{ userProfile.company_name }}</span>
                </div>
                <div v-if="userProfile.role" class="profile-info-item">
                  <span class="info-label">Роль:</span>
                  <span class="info-value">{{ userProfile.role }}</span>
                </div>
                <div v-if="userProfile.website" class="profile-info-item">
                  <span class="info-label">Сайт:</span>
                  <a :href="userProfile.website" class="info-link" target="_blank">{{ userProfile.website }}</a>
                </div>
              </div>
            </div>

            <!-- Дополнительная информация -->
            <div class="profile-section"
              v-if="userProfile.about_me || userProfile.about_business || userProfile.help_others || userProfile.community_request">
              <h3 class="profile-section-title">Дополнительно</h3>

              <div v-if="userProfile.about_me" class="profile-text-block">
                <h4 class="text-block-title">О себе</h4>
                <p class="text-block-content">{{ userProfile.about_me }}</p>
              </div>

              <div v-if="userProfile.about_business" class="profile-text-block">
                <h4 class="text-block-title">О бизнесе</h4>
                <p class="text-block-content">{{ userProfile.about_business }}</p>
              </div>

              <div v-if="userProfile.help_others" class="profile-text-block">
                <h4 class="text-block-title">Чем могу быть полезен</h4>
                <p class="text-block-content">{{ userProfile.help_others }}</p>
              </div>

              <div v-if="userProfile.community_request" class="profile-text-block">
                <h4 class="text-block-title">Запрос к сообществу</h4>
                <p class="text-block-content">{{ userProfile.community_request }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Нижняя навигация в стиле скетча -->
      <div class="bottom-nav-sketch">
        <div class="nav-container-sketch">
          <button v-for="navItem in navigation" :key="navItem.id" @click="currentPage = navItem.id"
            :class="['nav-button-sketch', currentPage === navItem.id ? 'nav-active-sketch' : '']">
            <div class="nav-icon-sketch">
              <BookOpen v-if="navItem.icon === 'BookOpen'" class="w-6 h-6" />
              <Heart v-if="navItem.icon === 'Heart'" class="w-6 h-6" />
              <BarChart3 v-if="navItem.icon === 'BarChart3'" class="w-6 h-6" />
              <User v-if="navItem.icon === 'User'" class="w-6 h-6" />
              <Handshake v-if="navItem.icon === 'Handshake'" class="w-6 h-6" />
            </div>
            <span class="nav-label-sketch">{{ navItem.name }}</span>
            <div v-if="currentPage === navItem.id" class="nav-underline-sketch"></div>
          </button>
        </div>
      </div>

      <!-- Модальное окно в стиле журнальной статьи -->
      <div v-if="selectedMaterialData" class="modal-overlay-sketch" @click="selectedMaterialData = null">
        <div class="modal-magazine" @click.stop>
          <!-- Заголовок статьи -->
          <div class="magazine-header">
            <!--<div class="magazine-date">ПОТОК</div>-->
            <h2 class="magazine-title" v-html="parseMarkdownV2(selectedMaterialData.description)"></h2>
            <div class="magazine-type" :class="getTypeClass(selectedMaterialData.type)">
              {{ selectedMaterialData.type }}
            </div>
            <button @click="selectedMaterialData = null" class="close-sketch">×</button>
          </div>

          <!-- Содержимое статьи -->
          <div class="magazine-content">
            <div class="magazine-text">
              <p v-html="parseMarkdownV2(selectedMaterialData.text || '')"></p>
            </div>

            <!-- Информационные блоки -->
            <div class="info-blocks-sketch">
              <div class="info-block" :hidden="selectedMaterialData.category ? false : true">
                <div class="block-title">Раздел:</div>
                <div class="block-value">{{ getCategoryName(selectedMaterialData.category) }}</div>
              </div>
              <div class="info-block">
                <div class="block-title">Дата:</div>
                <div class="block-value">{{ getDate(selectedMaterialData.created_at) }}</div>
              </div>
              <div class="info-block">
                <div class="block-title">Реакции:</div>
                <div class="block-value" style="font-size: 2rem; letter-spacing: -20px;">{{
                  selectedMaterialData.reactions ? selectedMaterialData.reactions.join('') : "-" }}</div>
              </div>
            </div>

            <!-- Теги в виде стикеров -->
            <div class="tags-stickers">
              <span v-for="tagId in selectedMaterialData.tags" :key="tagId" class="tag-sticker">
                {{ getTagName(tagId) }}
              </span>
            </div>

            <!-- Кнопки действий -->
            <div class="action-buttons-sketch">
              <button class="primary-button-sketch" @click="openLink()">
                {{ selectedMaterialData.type === 'Видео' ? 'Смотреть' : selectedMaterialData.type === 'Подкаст' ?
                  'Слушать' : 'Читать' }}
              </button>
              <div class="secondary-buttons-sketch">
                <button @click="toggleFavorite(selectedMaterialData.id)"
                  :class="['secondary-button-sketch', selectedMaterialData.isFavorite ? 'button-active' : '']">
                  {{ selectedMaterialData.isFavorite ? '❤️ В избранном' : '🤍 В избранное' }}
                </button>
                <button @click="toggleCompleted(selectedMaterialData.id)"
                  :class="['secondary-button-sketch', selectedMaterialData.completed ? 'button-active' : '']">
                  {{ selectedMaterialData.completed ? '✅ Пройдено' : '🔳 Отметить' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Модальное окно подобранного мэтчинга -->
      <div v-if="currentMatchingData && currentMatchingData.isNew" class="modal-overlay-sketch"
        @click="currentMatchingData = null">
        <div class="modal-magazine" @click.stop>
          <div class="match-card-header">
            <h2 class="match-card-title">Новый мэтч!</h2>
          </div>
          <div class="match-card-content">
            <div class="match-card-info">
              <div class="match-card-info-item">
                <img :src="currentMatchingData.user1_avatar" alt="Avatar" class="match-card-avatar">
                <span class="match-card-name">{{ currentMatchingData.user1_name }}</span>
                <span class="match-card-city">{{ currentMatchingData.user1_city }}</span>
                <span class="match-card-role">{{ currentMatchingData.user1_role }}</span>
                <span class="match-card-company">{{ currentMatchingData.user1_company_name }}</span>
                <span class="match-card-about">{{ currentMatchingData.user1_about_me }}</span>
              </div>
              <div class="match-card-info-item">
                <button class="match-card-button" @click="viewMatch(currentMatchingData.id)">
                  Написать в Telegram
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import ProgressSpinner from 'primevue/progressspinner';
import { BookOpen, Heart, User, BarChart3, Send, Instagram, Handshake } from 'lucide-vue-next'
import axios from 'axios'

//const user = ref(null)

const dataLoaded = ref(false)
const searchQuery = ref('')
const selectedCategory = ref({ id: 'all', name: 'Все' })
const selectedTag = ref('all')
const currentPage = ref('explore')
//const favoriteSortBy = ref('recent')
const selectedMaterialData = ref(null)

const materials = ref([])
const favoriteMaterials = ref([])
const completedMaterials = ref([])

const url = new URL(window.location.href)
const userId = ref(url.searchParams.get('userId') || null)
const userProfile = ref(null)
const isCreatingProfile = ref(false)
const profileForm = ref({
  first_name: '',
  last_name: '',
  birth_date: '',
  city: '',
  phone: '',
  show_phone: false,
  telegram: '',
  instagram: '',
  niche: '',
  company_name: '',
  role: '',
  website: '',
  about_me: '',
  about_business: '',
  help_others: '',
  community_request: '',
  avatar: '',
  contacts: ''
})

// Состояние для мэтчинга
/*
const currentCardIndex = ref(0)
const isDragging = ref(false)
const startX = ref(0)
const startY = ref(0)
const currentX = ref(0)
const currentY = ref(0)
*/
//const cardTransform = ref('')

// Тестовые данные для мэтчинга
const profiles = ref([])
const likedProfiles = ref([])
const showLikedProfiles = ref(false)
const searchQueryProfiles = ref('')
const currentMatchingData = ref(null)

const filteredProfiles = computed(() => {
  let filtered = profiles.value
  if (showLikedProfiles.value) {
    filtered = filtered.filter(profile => userProfile.value.contacts.includes(String(profile.id)))
  }
  if (searchQueryProfiles.value.trim()) {
    console.log(searchQueryProfiles.value)
    const query = searchQueryProfiles.value?.toLowerCase() || ''
    filtered = filtered.filter(profile =>
      profile.first_name?.toLowerCase().includes(query) ||
      profile.last_name?.toLowerCase().includes(query) ||
      profile.city?.toLowerCase().includes(query) ||
      profile.role?.toLowerCase().includes(query) ||
      profile.company_name?.toLowerCase().includes(query) ||
      profile.about_me?.toLowerCase().includes(query) ||
      profile.about_business?.toLowerCase().includes(query) ||
      profile.help_others?.toLowerCase().includes(query) ||
      profile.community_request?.toLowerCase().includes(query) ||
      profile.niche?.toLowerCase().includes(query) ||
      profile.instagram?.toLowerCase().includes(query) ||
      profile.telegram?.toLowerCase().includes(query) ||
      profile.website?.toLowerCase().includes(query)
    )
  }

  return filtered
})

// Вычисляемые свойства для мэтчинга
//const visibleCards = computed(() => {
//  return profiles.value.slice(currentCardIndex.value, currentCardIndex.value + 3)
//})

/*
const selectCard = (profile, index) => {
  currentCardIndex.value = index
}

const likeCard = () => {
  if (currentCardIndex.value < profiles.value.length) {
    // Здесь можно добавить логику сохранения лайка
    console.log('Лайк:', profiles.value[currentCardIndex.value])
    currentCardIndex.value++
  }
}
  */

// Данные для выпадающих списков
const cities = [
  'Москва', 'Санкт-Петербург', 'Новосибирск', 'Екатеринбург', 'Казань',
  'Нижний Новгород', 'Челябинск', 'Самара', 'Омск', 'Ростов-на-Дону',
  'Уфа', 'Красноярск', 'Воронеж', 'Пермь', 'Волгоград', 'Краснодар',
  'Саратов', 'Тюмень', 'Тольятти', 'Ижевск', 'Барнаул', 'Ульяновск',
  'Иркутск', 'Хабаровск', 'Ярославль', 'Владивосток', 'Махачкала',
  'Томск', 'Оренбург', 'Кемерово', 'Новокузнецк', 'Рязань', 'Астрахань',
  'Пенза', 'Липецк', 'Тула', 'Киров', 'Чебоксары', 'Калининград'
]

const niches = [
  'IT и технологии', 'Маркетинг и реклама', 'Финансы и банки', 'Образование',
  'Здравоохранение', 'Розничная торговля', 'E-commerce', 'Недвижимость',
  'Строительство', 'Производство', 'Логистика', 'Туризм и гостеприимство',
  'Красота и здоровье', 'Спорт и фитнес', 'Питание и рестораны',
  'Автомобильная индустрия', 'Медиа и развлечения', 'Консалтинг',
  'Юридические услуги', 'Дизайн и творчество', 'Сельское хозяйство',
  'Энергетика', 'Телекоммуникации', 'Фармацевтика', 'Другое'
]

const roles = [
  'Основатель/CEO', 'Соучредитель', 'Директор', 'Заместитель директора',
  'Руководитель отдела', 'Менеджер проекта', 'Старший специалист',
  'Специалист', 'Консультант', 'Фрилансер', 'Предприниматель',
  'Инвестор', 'Студент', 'Другое'
]

async function loadTelegramSDK() {
  return new Promise((resolve, reject) => {
    if (window.Telegram && window.Telegram.WebApp) {
      return resolve(window.Telegram.WebApp)
    }

    const script = document.createElement('script')
    script.src = 'https://telegram.org/js/telegram-web-app.js'
    script.onload = () => {
      if (window.Telegram && window.Telegram.WebApp) {
        resolve(window.Telegram.WebApp)
      } else {
        reject(new Error('Telegram SDK не инициализировался'))
      }
    }
    script.onerror = () => reject(new Error('Ошибка загрузки Telegram SDK'))
    document.head.appendChild(script)
  })
}

// Загрузка сохраненной темы при монтировании
onMounted(async () => {
  try {

    const tg = await loadTelegramSDK()
    tg.ready()

    /*
    if (process.env.NODE_ENV === 'development') {
      require('../mockTelegram');
    }

    const tg = window.Telegram?.WebApp
    */

    console.log('[Telegram] tg:', tg)
    const initData = tg.initData
    console.log('[Telegram] initData:', initData)
    console.log('[Telegram] initDataUnsafe:', tg.initDataUnsafe)

    if (!initData || !tg.initDataUnsafe?.user) {
      console.warn('[Telegram] initData отсутствует — приложение не запущено внутри Telegram.')

      alert('Приложение не запущено внутри Telegram. Пожалуйста, откройте его в Telegram.')
      return
    }

    localStorage.setItem('theme', 'light')

    const result = await axios.post(`https://ncht.bz/api/potok/init-app`, {
      initData: tg.initData,
      date: new Date().toISOString().split('T')[0]
    })

    if (result.data.success) {
      userId.value = tg.initDataUnsafe.user.id
      userProfile.value = result.data.data.user
      profiles.value = result.data.data.profiles
      currentMatchingData.value = result.data.data.matches
      likedProfiles.value = result.data.data.user.contacts ? result.data.data.profiles.filter(profile => result.data.data.user.contacts.includes(profile.id)) : []
      console.log('[Auth] Пользователь успешно авторизован')
    } else {
      console.error('[Auth] Ошибка авторизации')
    }

    const request = await axios.get(`https://ncht.bz/api/tg/client/get-posts`, {
      params: {
        chatId: '"-1002586005268"'
      }
    })
    materials.value = request.data.posts

    if (userProfile.value.favorites) {
      favoriteMaterials.value = materials.value.filter(material => userProfile.value.favorites.includes(String(material.id)))
    }
    if (userProfile.value.completed) {
      completedMaterials.value = materials.value.filter(material => userProfile.value.completed.includes(String(material.id)))
    }

    materials.value.forEach(post => {
      post.isFavorite = favoriteMaterials.value.some(m => m.id === post.id)
      post.completed = completedMaterials.value.some(m => m.id === post.id)
    })
    document.body.style.overflow = 'auto'
    dataLoaded.value = true
  } catch (err) {
    console.error('[SDK/Auth] Ошибка:', err)
  }
})


/*
onMounted(async () => {
  try {
    const request = await axios.get(`https://ncht.bz/api/tg/client/get-posts`, {
      params: {
        chatId: '"-1002586005268"'
      }
    })
    materials.value = request.data.posts
    if (userId.value) {
      const userRequest = await axios.get(`https://ncht.bz/api/tg/client/get-user`, {
        params: {
          userId: parseInt(userId.value)
        }
      })
      if (userRequest.data.user.favorites) {
        favoriteMaterials.value = materials.value.filter(material => userRequest.data.user.favorites.includes(String(material.id)))
      }
      if (userRequest.data.user.completed) {
        completedMaterials.value = materials.value.filter(material => userRequest.data.user.completed.includes(String(material.id)))
      }
    }
    
  } catch (error) {
    console.error('Ошибка при получении постов:', error)
    materials.value = []
  }
})*/

const viewMatch = async (matchId) => {
  const tg = window.Telegram?.WebApp
  const response = await axios.post('https://ncht.bz/api/potok/view-match', {
    initData: tg.initData,
    matchId: matchId
  })
  if (response.data.success) {
    currentMatchingData.value = null
    window.location.href = `https://t.me/${tg.initDataUnsafe.user.username}`
  }
}

// Методы для профиля
const startCreatingProfile = () => {
  isCreatingProfile.value = true
  const tg = window.Telegram?.WebApp
  if (tg?.initDataUnsafe?.user) {
    profileForm.value.firstName = tg.initDataUnsafe.user.first_name || ''
    profileForm.value.lastName = tg.initDataUnsafe.user.last_name || ''
    profileForm.value.telegram = tg.initDataUnsafe.user.username || ''
    profileForm.value.avatar = tg.initDataUnsafe.user.photo_url || ''
  }
}

const cancelProfileCreation = () => {
  isCreatingProfile.value = false
  // Очистить форму
  Object.keys(profileForm.value).forEach(key => {
    if (typeof profileForm.value[key] === 'boolean') {
      profileForm.value[key] = false
    } else {
      profileForm.value[key] = ''
    }
  })
}

const validatePhone = (phone) => {
  // Убираем все пробелы, дефисы и скобки
  const cleanPhone = phone.replace(/[\s\-()]/g, '')

  // Регулярное выражение для международных номеров
  // Поддерживает форматы: +1234567890, 1234567890, +1-234-567-8900
  const phoneRegex = /^(\+?[1-9]\d{0,14})$/

  return phoneRegex.test(cleanPhone)
}

const saveProfile = async () => {
  // Простая валидация
  if (!profileForm.value.first_name.trim()) {
    alert('Пожалуйста, укажите ваше имя')
    return
  }

  if (!profileForm.value.last_name.trim()) {
    alert('Пожалуйста, укажите вашу фамилию')
    return
  }

  if (profileForm.value.phone && !validatePhone(profileForm.value.phone)) {
    alert('Пожалуйста, укажите корректный номер телефона')
    return
  }

  // Сохранить профиль
  userProfile.value = { ...profileForm.value }
  const tg = window.Telegram?.WebApp
  const response = await axios.post('https://ncht.bz/api/potok/add-user-anketa', {
    initData: tg.initData,
    firstName: profileForm.value.first_name,
    lastName: profileForm.value.last_name,
    birthDate: profileForm.value.birth_date,
    city: profileForm.value.city,
    phone: profileForm.value.phone,
    showPhone: profileForm.value.show_phone,
    telegram: profileForm.value.telegram,
    instagram: profileForm.value.instagram,
    niche: profileForm.value.niche,
    companyName: profileForm.value.company_name,
    role: profileForm.value.role,
    website: profileForm.value.website,
    aboutMe: profileForm.value.about_me,
    aboutBusiness: profileForm.value.about_business,
    helpOthers: profileForm.value.help_others,
    communityRequest: profileForm.value.community_request,
    avatar: profileForm.value.avatar
  })

  if (!response.data.success) {
    alert('Ошибка при сохранении профиля')
    return
  }

  isCreatingProfile.value = false

  alert('Профиль успешно сохранен!')
}

const editProfile = () => {
  profileForm.value = { ...userProfile.value }
  isCreatingProfile.value = true
}

const getCharacterCount = (text, maxLength) => {
  return `${text.length}/${maxLength}`
}

// Методы для мэтчинга
const getAge = (birthDate) => {
  if (!birthDate) return null
  const today = new Date()
  const birth = new Date(birthDate)
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return age
}

// Категории (разделы)
const categories = [
  { text: 'all', name: 'Все' },
  { text: 'команда', name: '#команда', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
  { text: 'продажи', name: '#продажи', icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4' },
  { text: 'маркетинг', name: '#маркетинг', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'ИИ', name: '#ИИ', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'стратегия', name: '#стратегия', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { text: 'безопасность', name: '#безопасность', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'продукт', name: '#продукт', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'мышление', name: '#мышление', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'лайф', name: '#лайф', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'разбор', name: '#разбор', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'Дашкиев', name: '#Дашкиев', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'Калашников', name: '#Калашников', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { text: 'эксперт', name: '#эксперт', icon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' }
]

// Теги
const tags = [
  { id: 'all', name: 'Все' },
  { id: 'text', name: 'Текст' },
  { id: 'video', name: 'Видео' },
  { id: 'podcast', name: 'Подкасты' }
]

// Навигация
const navigation = [
  { id: 'explore', name: 'Курсы', icon: 'BookOpen' },
  { id: 'favorites', name: 'Избранное', icon: 'Heart' },
  { id: 'matching', name: 'Мэтчинг', icon: 'Handshake' },
  { id: 'progress', name: 'Прогресс', icon: 'BarChart3' },
  { id: 'profile', name: 'Профиль', icon: 'User' }
]

const filteredMaterials = computed(() => {
  let filtered = materials.value
  if (selectedCategory.value.name !== 'Все') {
    filtered = filtered.filter(material => material.categories.includes(selectedCategory.value.name.slice(1)))
  }

  if (selectedTag.value !== 'all') {
    filtered = filtered.filter(material => material.tag && material.tag == selectedTag.value)
  }

  if (searchQuery.value.trim()) {
    console.log(searchQuery.value)
    const query = searchQuery.value?.toLowerCase() || ''
    filtered = filtered.filter(material =>
      material.no_formatted?.message?.toLowerCase().includes(query)
    )
  }

  return filtered
})

const sortedFavorites = computed(() => {
  const favorites = [...favoriteMaterials.value]
  return favorites.reverse()
})

// Методы
const selectMaterial = (material) => {
  selectedMaterialData.value = material
}

const toggleFavorite = async (id) => {
  const material = materials.value.find(m => m.id === id)
  if (material) {
    if (material.isFavorite) {
      favoriteMaterials.value = favoriteMaterials.value.filter(m => m.id !== id)
    } else {
      favoriteMaterials.value.push(material)
    }
    material.isFavorite = !material.isFavorite
    if (userId.value) {
      await axios.post(`https://ncht.bz/api/tg/client/update-user`, {
        userId: parseInt(userId.value),
        favorites: favoriteMaterials.value.map(m => m.id)
      })
    }
  }
}

const toggleCompleted = (id) => {
  const material = materials.value.find(m => m.id === id)
  if (material) {
    material.completed = !material.completed
    const completed = JSON.parse(localStorage.getItem('completed') || '[]')
    if (material.completed) {
      localStorage.setItem('completed', JSON.stringify([...completed, id]))
    } else {
      localStorage.setItem('completed', JSON.stringify(completed.filter(id => id !== id)))
    }
  }
}

const toggleLikeProfile = async (id) => {
  if (!userProfile.value.contacts) {
    userProfile.value.contacts = []
  }
  if (userProfile.value.contacts.includes(String(id))) {
    userProfile.value.contacts = userProfile.value.contacts.filter(contact => contact !== String(id))
  } else {
    userProfile.value.contacts.push(String(id))
  }
  await axios.post(`https://ncht.bz/api/potok/update-user`, {
    userId: parseInt(userId.value),
    contacts: userProfile.value.contacts
  }).then(response => {
    console.log(response)
  })
}

const getDate = (date) => {
  return new Date(date * 1000).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

const openLink = () => {
  window.location.href = selectedMaterialData.value.tg_link;
}

const getTypeClass = (type) => {
  switch (type) {
    case 'Видео': return 'type-video'
    case 'Статья': return 'type-article'
    case 'Подкаст': return 'type-podcast'
    default: return 'type-unknown'
  }
}
const parseMarkdownV2 = (text) => {
  if (!text) return ''

  return text
    // Экранируем спецсимволы
    //.replace(/([_*[\]()~`>#+\-=|{}.!])/g, '\\$1')
    // Жирный текст
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Курсив
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    // Подчеркнутый
    .replace(/__(.*?)__/g, '<u>$1</u>')
    // Зачеркнутый
    .replace(/~~(.*?)~~/g, '<s>$1</s>')
    // Моноширинный
    .replace(/`(.*?)`/g, '<code>$1</code>')
    // Ссылки
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    // Перенос строки
    .replace(/\n/g, '<br>')
}

const getCategoryName = (categoryId) => {
  const category = categories.find(c => c.text === categoryId)
  return category ? category.name : 'Неизвестно'
}

const getTagName = (tagId) => {
  const tag = tags.find(t => t.id === tagId)
  return tag ? tag.name : 'Неизвестно'
}

const getCategoryProgress = (category) => {
  if (category === 'all' || category?.name === 'Все') {
    return getOverallProgress()
  }

  const categoryName = typeof category === 'string' ? category : category?.name?.slice(1)
  const categoryMaterials = materials.value.filter(m => m.categories.includes(categoryName))
  if (categoryMaterials.length === 0) return 0

  const completedCount = categoryMaterials.filter(m => m.completed).length
  return Math.round((completedCount / categoryMaterials.length) * 100)
}

const getOverallProgress = () => {
  if (materials.value.length === 0) return 0

  const completedCount = materials.value.filter(m => m.completed).length
  return Math.round((completedCount / materials.value.length) * 100)
}

const getRandomRotation = (index, seed = 0) => {
  const rotations = [-2, -1, 0, 1, 2, -1.5, 1.5]
  return rotations[(index + seed) % rotations.length]
}
/*
const getBorderWidth = () => {
  // Implement your logic to calculate border width
  return 300; // Replace with actual calculation
}

const getBorderHeight = () => {
  // Implement your logic to calculate border height
  return 200; // Replace with actual calculation
}*/

const openedProfileIndex = ref(null)

const openProfileModal = (index) => {
  openedProfileIndex.value = index
}
const closeProfileModal = () => {
  openedProfileIndex.value = null
}
</script>

<style scoped>
body {
  margin: 0px;
  overflow: hidden;
}
/* Базовые стили */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Шрифты в стиле рукописного текста */
@import url('https://fonts.googleapis.com/css2?family=Kalam:wght@300;400;700&family=Caveat:wght@400;500;600;700&display=swap');

/* Контейнер приложения */
.app-container {
  min-height: 100vh;
  background: #f8f6f0;
  color: #2d2d2d;
  font-family: 'Kalam', cursive;
  position: relative;
  transition: all 0.3s ease;
}

/* Темная тема */
.app-container.dark-theme {
  background: #1a1a1a;
  color: #e0e0e0;
}

/* Фоновая текстура */
.app-container::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(120, 219, 255, 0.03) 0%, transparent 50%);
  pointer-events: none;
}

.dark-theme::before {
  background-image:
    radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(120, 219, 255, 0.08) 0%, transparent 50%);
}

/* Кнопка смены темы */
.theme-toggle-sketch {
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 100;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  transform: rotate(-2deg);
}

.theme-toggle-sketch:hover {
  transform: rotate(0deg);
  background: rgba(255, 255, 255, 0.1);
}

.dark-theme .theme-toggle-sketch:hover {
  background: rgba(255, 255, 255, 0.05);
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.sun-icon,
.moon-icon {
  width: 20px;
  height: 20px;
  color: #999;
  transition: all 0.3s ease;
}

.sun-icon.active {
  color: #ffa726;
  transform: scale(1.1);
}

.moon-icon.active {
  color: #42a5f5;
  transform: scale(1.1);
}

.toggle-divider {
  font-family: 'Caveat', cursive;
  font-size: 1.2rem;
  color: #999;
  transform: rotate(15deg);
}

.toggle-label {
  font-family: 'Caveat', cursive;
  font-size: 0.8rem;
  color: #666;
  text-align: center;
  margin-bottom: 0.25rem;
}

.dark-theme .toggle-label {
  color: #999;
}

.toggle-arrow {
  width: 20px;
  height: 15px;
  color: #ccc;
  margin: 0 auto;
}

/* Контейнер */
.tg-app-container {
  max-width: 28rem;
  margin: 0 auto;
  padding: 1.5rem 1rem;
  position: relative;
  z-index: 1;
  overflow: auto;
}

/* Шапка в стиле скетча */
.header {
  margin-bottom: 2rem;
  text-align: center;
}

.header-sketch {
  position: relative;
  padding: 1rem;
}

.month-badge {
  display: inline-block;
  background: #2d2d2d;
  color: white;
  padding: 0.25rem 0.75rem;
  font-family: 'Caveat', cursive;
  font-weight: 700;
  font-size: 1rem;
  transform: rotate(-2deg);
  margin-bottom: 0.5rem;
}

.dark-theme .month-badge {
  background: #e0e0e0;
  color: #1a1a1a;
}

.main-title {
  font-family: 'Caveat', cursive;
  font-size: 2rem;
  font-weight: 700;
  color: #2d2d2d;
  margin-bottom: 0.5rem;
  transform: rotate(1deg);
}

.dark-theme .main-title {
  color: #e0e0e0;
}

.subtitle-arrow {
  position: relative;
  margin-top: 1rem;
}

.arrow-sketch {
  width: 120px;
  height: 30px;
  color: #666;
  margin-bottom: 0.5rem;
}

.dark-theme .arrow-sketch {
  color: #999;
}

.subtitle-text {
  font-size: 0.9rem;
  color: #666;
  font-style: italic;
}

.dark-theme .subtitle-text {
  color: #999;
}

.sketch-elements {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  gap: 1rem;
}

.brain-sketch,
.lightbulb-sketch {
  width: 40px;
  height: 40px;
  color: #999;
}

.dark-theme .brain-sketch,
.dark-theme .lightbulb-sketch {
  color: #666;
}

/* Поиск в рукописном стиле */
.search-container {
  margin-bottom: 2rem;
}

.search-sketch-wrapper {
  position: relative;
}

.search-label {
  font-family: 'Caveat', cursive;
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 0.5rem;
  transform: rotate(-1deg);
}

.dark-theme .search-label {
  color: #999;
}

.search-input-wrapper {
  position: relative;
}

.search-input-sketch {
  width: 100%;
  background: transparent;
  border: none;
  padding: 0.75rem 0;
  font-family: 'Kalam', cursive;
  font-size: 1rem;
  color: #2d2d2d;
  outline: none;
}

.dark-theme .search-input-sketch {
  color: #e0e0e0;
}

.search-input-sketch::placeholder {
  color: #999;
  font-style: italic;
}

.dark-theme .search-input-sketch::placeholder {
  color: #666;
}

.search-underline {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: #2d2d2d;
  transform: rotate(-0.5deg);
}

.dark-theme .search-underline {
  background: #e0e0e0;
}

/* Разделы в стиле коллажа */
.categories-collage {
  margin-bottom: 2rem;
}

.collage-title {
  position: relative;
  margin-bottom: 1.5rem;
  text-align: center;
}

.handwritten-text {
  font-family: 'Caveat', cursive;
  font-size: 1.3rem;
  color: #2d2d2d;
  font-weight: 600;
}

.dark-theme .handwritten-text {
  color: #e0e0e0;
}

.underline-sketch {
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 200px;
  height: 10px;
  color: #666;
}

.dark-theme .underline-sketch {
  color: #999;
}

.categories-grid-sketch {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.category-card-sketch {
  position: relative;
  background: white;
  border-radius: 8px;
  padding: 1.5rem 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.dark-theme .category-card-sketch {
  background: #2d2d2d;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.category-card-sketch:hover {
  transform: rotate(0deg) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.dark-theme .category-card-sketch:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.category-active-sketch {
  background: #fff8e1;
  box-shadow: 0 4px 16px rgba(255, 193, 7, 0.2);
}

.dark-theme .category-active-sketch {
  background: #3d3d00;
  box-shadow: 0 4px 16px rgba(255, 193, 7, 0.3);
}

.sketch-frame {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.frame-border {
  width: 100%;
  height: 100%;
  color: #ddd;
}

.dark-theme .frame-border {
  color: #555;
}

.category-content-sketch {
  position: relative;
  z-index: 2;
  text-align: center;
}

.category-icon-sketch {
  width: 3rem;
  height: 3rem;
  margin: 0 auto 0.75rem;
  background: #f5f5f5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dark-theme .category-icon-sketch {
  background: #1a1a1a;
}

.category-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: #666;
}

.dark-theme .category-icon {
  color: #999;
}

.category-name-handwritten {
  font-family: 'Caveat', cursive;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d2d2d;
  margin-bottom: 0.75rem;
}

.dark-theme .category-name-handwritten {
  color: #e0e0e0;
}

.progress-sketch {
  text-align: center;
}

.progress-label {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.25rem;
}

.dark-theme .progress-label {
  color: #999;
}

.progress-line {
  width: 100px;
  height: 8px;
  color: #4caf50;
}

.category-sticker {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: #ff6b6b;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: bold;
  transform: rotate(15deg);
}

/* Фильтры в стиле заметок */
.tags-notes {
  margin-bottom: 2rem;
}

.notes-title {
  font-family: 'Caveat', cursive;
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 1rem;
  transform: rotate(-1deg);
}

.dark-theme .notes-title {
  color: #999;
}

.tag-notes-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.tag-note {
  position: relative;
  background: #fff3cd;
  border: none;
  padding: 0.5rem 1rem;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  color: #856404;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: rotate(-1deg);
}

.dark-theme .tag-note {
  background: #4a4a00;
  color: #ffeb3b;
}

.tag-note:hover {
  transform: rotate(0deg);
  background: #ffeaa7;
}

.dark-theme .tag-note:hover {
  background: #5a5a00;
}

.tag-note-active {
  background: #fd79a8;
  color: white;
  transform: rotate(2deg);
}

.dark-theme .tag-note-active {
  background: #c2185b;
}

.note-corner {
  position: absolute;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-top: 8px solid #ddd;
}

.dark-theme .note-corner {
  border-top-color: #555;
}

/* Материалы в стиле журнала */
.materials-magazine {
  margin-bottom: 6rem;
}

.material-article {
  position: relative;
  background: white;
  margin-bottom: 2rem;
  padding: 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.dark-theme .material-article {
  background: #2d2d2d;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.material-article:hover {
  transform: rotate(0deg) !important;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.dark-theme .material-article:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.article-frame {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.article-border {
  width: 100%;
  height: 100%;
  color: #eee;
}

.dark-theme .article-border {
  color: #555;
}

.article-content {
  position: relative;
  z-index: 2;
}

.article-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.article-type-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.type-video {
  background: #e3f2fd;
  color: #1976d2;
}

.dark-theme .type-video {
  background: #0d47a1;
  color: #bbdefb;
}

.type-article {
  background: #e8f5e8;
  color: #388e3c;
}

.dark-theme .type-article {
  background: #1b5e20;
  color: #c8e6c9;
}

.type-podcast {
  background: #fff3e0;
  color: #f57c00;
}

.dark-theme .type-podcast {
  background: #e65100;
  color: #ffe0b2;
}

.article-title {
  flex: 1;
  font-family: 'Caveat', cursive;
  font-size: 1.3rem;
  font-weight: 600;
  color: #2d2d2d;
  line-height: 1.2;
}

.dark-theme .article-title {
  color: #e0e0e0;
}

.paperclip-favorite {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #ccc;
  transition: all 0.3s ease;
}

.paperclip-favorite:hover {
  color: #666;
  transform: rotate(15deg);
}

.dark-theme .paperclip-favorite:hover {
  color: #999;
}

.paperclip-active {
  color: #ff6b6b;
  transform: rotate(15deg);
}

.paperclip-favorite svg {
  width: 20px;
  height: 30px;
}

.article-description {
  font-family: 'Kalam', cursive;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 1rem;
}

.dark-theme .article-description {
  color: #999;
}

.article-stats-sketch {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.85rem;
}

.stat-sketch {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #666;
}

.dark-theme .stat-sketch {
  color: #999;
}

.stat-icon {
  font-size: 1rem;
}

.checkbox-sketch {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Kalam', cursive;
  font-size: 0.85rem;
  color: #666;
  transition: all 0.3s ease;
}

.dark-theme .checkbox-sketch {
  color: #999;
}

.checkbox-sketch:hover {
  color: #2d2d2d;
}

.dark-theme .checkbox-sketch:hover {
  color: #e0e0e0;
}

.checkbox-checked {
  color: #4caf50;
}

.checkbox-sketch svg {
  width: 20px;
  height: 20px;
}

.article-arrow {
  position: absolute;
  top: -15px;
  right: -15px;
  width: 50px;
  height: 30px;
  color: #ddd;
}

.dark-theme .article-arrow {
  color: #555;
}

/* Страница избранного в стиле доски объявлений */
.favorites-board {
  margin-bottom: 6rem;
}

.board-header {
  position: relative;
  text-align: center;
  margin-bottom: 2rem;
}

.board-title {
  font-family: 'Caveat', cursive;
  font-size: 2rem;
  font-weight: 700;
  color: #2d2d2d;
  transform: rotate(-1deg);
}

.dark-theme .board-title {
  color: #e0e0e0;
}

.pushpin {
  position: absolute;
  top: -10px;
  right: 50%;
  transform: translateX(50%);
  width: 12px;
  height: 12px;
  background: #ff6b6b;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.empty-board {
  text-align: center;
  padding: 3rem 1rem;
}

.empty-note {
  position: relative;
  background: #fff3cd;
  padding: 2rem;
  border-radius: 4px;
  transform: rotate(-2deg);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.dark-theme .empty-note {
  background: #4a4a00;
  color: #ffeb3b;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.note-tape {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 16px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 2px;
}

.dark-theme .note-tape {
  background: rgba(255, 255, 255, 0.2);
}

.back-button-sketch {
  background: #2d2d2d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 1rem;
  transform: rotate(1deg);
  transition: all 0.3s ease;
}

.dark-theme .back-button-sketch {
  background: #e0e0e0;
  color: #1a1a1a;
}

.back-button-sketch:hover {
  transform: rotate(0deg);
  background: #1a1a1a;
}

.dark-theme .back-button-sketch:hover {
  background: #f5f5f5;
}

.favorites-grid-board {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.favorite-note {
  position: relative;
  background: #fff;
  padding: 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.dark-theme .favorite-note {
  background: #2d2d2d;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.favorite-note:hover {
  transform: rotate(0deg) !important;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.dark-theme .favorite-note:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.note-content {
  position: relative;
  z-index: 2;
}

.note-type {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
}

.note-title {
  font-family: 'Caveat', cursive;
  font-size: 1.2rem;
  font-weight: 600;
  color: #2d2d2d;
  margin-bottom: 0.5rem;
}

.dark-theme .note-title {
  color: #e0e0e0;
}

.note-desc {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.3;
  margin-bottom: 0.75rem;
}

.dark-theme .note-desc {
  color: #999;
}

.note-stats {
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: #999;
}

.dark-theme .note-stats {
  color: #666;
}

.remove-pin {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #ff6b6b;
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  transition: all 0.3s ease;
}

.remove-pin:hover {
  background: #e55555;
  transform: rotate(90deg);
}

/* Страница прогресса в стиле инфографики */
.progress-infographic {
  margin-bottom: 6rem;
}

.infographic-header {
  text-align: center;
  margin-bottom: 2rem;
}

.infographic-title {
  font-family: 'Caveat', cursive;
  font-size: 2rem;
  font-weight: 700;
  color: #2d2d2d;
  margin-bottom: 1rem;
  transform: rotate(-1deg);
}

.dark-theme .infographic-title {
  color: #e0e0e0;
}

.overall-progress-sketch {
  display: flex;
  justify-content: center;
}

.progress-circle {
  position: relative;
  width: 120px;
  height: 120px;
}

.progress-circle svg {
  width: 100%;
  height: 100%;
  color: #4caf50;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: 'Caveat', cursive;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d2d2d;
}

.dark-theme .progress-text {
  color: #e0e0e0;
}

.categories-progress-sketch {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: rotate(-0.5deg);
}

.dark-theme .categories-progress-sketch {
  background: #2d2d2d;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.section-title-sketch {
  font-family: 'Caveat', cursive;
  font-size: 1.3rem;
  font-weight: 600;
  color: #2d2d2d;
  margin-bottom: 1rem;
}

.dark-theme .section-title-sketch {
  color: #e0e0e0;
}

.progress-bars-sketch {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.progress-bar-sketch {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.bar-label {
  min-width: 120px;
  font-size: 0.9rem;
  color: #666;
}

.dark-theme .bar-label {
  color: #999;
}

.bar-container {
  flex: 1;
  position: relative;
  height: 20px;
  background: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
}

.dark-theme .bar-container {
  background: #1a1a1a;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #8bc34a);
  border-radius: 10px;
  transition: width 0.5s ease;
}

.bar-percent {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8rem;
  color: #666;
}

.dark-theme .bar-percent {
  color: #999;
}

/* Страница профиля */
.profile-sketch {
  margin-bottom: 6rem;
  display: flex;
  justify-content: center;
}

.profile-note {
  position: relative;
  background: #fff3cd;
  padding: 2rem;
  border-radius: 4px;
  transform: rotate(-2deg);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.dark-theme .profile-note {
  background: #4a4a00;
  color: #ffeb3b;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Нижняя навигация в стиле скетча */
.bottom-nav-sketch {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-top: 2px solid #eee;
  z-index: 10;
}

.dark-theme .bottom-nav-sketch {
  background: rgba(26, 26, 26, 0.95);
  border-top-color: #555;
}

.nav-container-sketch {
  display: flex;
  justify-content: space-around;
  padding: 0.75rem 0;
  max-width: 28rem;
  margin: 0 auto;
}

.nav-button-sketch {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  transition: all 0.3s ease;
  color: #999;
}

.nav-active-sketch {
  color: #2d2d2d;
}

.dark-theme .nav-active-sketch {
  color: #e0e0e0;
}

.nav-icon-sketch {
  width: 24px;
  height: 24px;
  margin-bottom: 0.25rem;
}

.nav-label-sketch {
  font-family: 'Kalam', cursive;
  font-size: 0.75rem;
}

.nav-underline-sketch {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: #2d2d2d;
  border-radius: 1px;
}

.dark-theme .nav-underline-sketch {
  background: #e0e0e0;
}

/* Модальное окно в стиле журнальной статьи */
.modal-overlay-sketch {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-magazine {
  background: white;
  width: 100%;
  max-width: 28rem;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  /*transform: rotate(-0.5deg);*/
}

.dark-theme .modal-magazine {
  background: #2d2d2d;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
}

.magazine-header {
  background: url('@/assets/images/magazine-header-bg.png') no-repeat center center !important;
  background-size: cover !important;
  position: relative;
  /*background: #f8f8f8 !important;*/
  padding: 1.5rem;
  border-bottom: 2px solid #eee;
  text-align: center;
  height: 300px;
}

.dark-theme .magazine-header {
  background: #1a1a1a;
  border-bottom-color: #555;
}

.magazine-date {
  background: #2d2d2d;
  color: white;
  padding: 0.25rem 0.75rem;
  font-family: 'Caveat', cursive;
  font-weight: 700;
  font-size: 0.9rem;
  display: inline-block;
  transform: rotate(-2deg);
  margin-bottom: 0.5rem;
}

.dark-theme .magazine-date {
  background: #e0e0e0;
  color: #1a1a1a;
}

.magazine-title {
  font-family: 'Caveat', cursive;
  font-size: 1.5rem;
  font-weight: 700;
  color: #e0e0e0;
  margin-bottom: 0.5rem;
  background-color: antiquewhite;
  border-radius: 1rem;
}

.dark-theme .magazine-title {
  color: #2d2d2d;
}

.magazine-type {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.close-sketch {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #999;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.close-sketch:hover {
  background: #f0f0f0;
  color: #2d2d2d;
}

.dark-theme .close-sketch:hover {
  background: #555;
  color: #e0e0e0;
}

.magazine-content {
  padding: 1.5rem;
}

.magazine-text {
  margin-bottom: 1.5rem;
  line-height: 1.6;
  color: #444;
}

.dark-theme .magazine-text {
  color: #ccc;
}

.info-blocks-sketch {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.info-block {
  background: #f8f8f8;
  padding: 1rem;
  border-radius: 4px;
  text-align: center;
  transform: rotate(-1deg);
}

.dark-theme .info-block {
  background: #1a1a1a;
}

.info-block:nth-child(even) {
  transform: rotate(1deg);
}

.block-title {
  font-size: 0.75rem;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
}

.dark-theme .block-title {
  color: #666;
}

.block-value {
  font-family: 'Caveat', cursive;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d2d2d;
}

.dark-theme .block-value {
  color: #e0e0e0;
}

.tags-stickers {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.tag-sticker {
  background: #fff3cd;
  color: #856404;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-family: 'Kalam', cursive;
  transform: rotate(-2deg);
}

.dark-theme .tag-sticker {
  background: #4a4a00;
  color: #ffeb3b;
}

.tag-sticker:nth-child(even) {
  transform: rotate(2deg);
}

.action-buttons-sketch {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.primary-button-sketch {
  background: #2d2d2d;
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 4px;
  font-family: 'Kalam', cursive;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transform: rotate(-1deg);
  transition: all 0.3s ease;
}

.dark-theme .primary-button-sketch {
  background: #e0e0e0;
  color: #1a1a1a;
}

.primary-button-sketch:hover {
  transform: rotate(0deg);
  background: #1a1a1a;
}

.dark-theme .primary-button-sketch:hover {
  background: #f5f5f5;
}

.secondary-buttons-sketch {
  display: flex;
  gap: 0.75rem;
}

.secondary-button-sketch {
  flex: 1;
  background: #f8f8f8;
  border: 1px solid #ddd;
  color: #666;
  padding: 0.75rem;
  border-radius: 4px;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: rotate(1deg);
}

.dark-theme .secondary-button-sketch {
  background: #1a1a1a;
  border-color: #555;
  color: #999;
}

.secondary-button-sketch:hover {
  transform: rotate(0deg);
  background: #f0f0f0;
  color: #2d2d2d;
}

.dark-theme .secondary-button-sketch:hover {
  background: #333;
  color: #e0e0e0;
}

.button-active {
  background: #e8f5e8;
  border-color: #4caf50;
  color: #2e7d32;
}

.dark-theme .button-active {
  background: #1b5e20;
  border-color: #4caf50;
  color: #c8e6c9;
}

.button-active:hover {
  background: #c8e6c9;
}

.dark-theme .button-active:hover {
  background: #2e7d32;
}

/* Адаптивные настройки */
@media (max-width: 640px) {
  .tg-app-container {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .categories-grid-sketch {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .favorites-grid-board {
    grid-template-columns: 1fr;
  }

  .info-blocks-sketch {
    grid-template-columns: 1fr;
  }

  .secondary-buttons-sketch {
    flex-direction: column;
  }

  .theme-toggle-sketch {
    top: 0.5rem;
    left: 0.5rem;
    padding: 0.5rem;
  }
}

/* Анимации */
@keyframes float {

  0%,
  100% {
    transform: translateY(0px) rotate(-2deg);
  }

  50% {
    transform: translateY(-5px) rotate(-2deg);
  }
}

.category-card-sketch:hover {
  animation: float 2s ease-in-out infinite;
}

.material-article:hover {
  animation: float 2s ease-in-out infinite;
}

/* Дополнительные декоративные элементы */
.sketch-decoration {
  position: absolute;
  pointer-events: none;
  color: #eee;
}

.dark-theme .sketch-decoration {
  color: #555;
}

/* Эффекты для рукописного стиля */
.handwritten-effect {
  position: relative;
}

.handwritten-effect::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 1px;
  background: currentColor;
  opacity: 0.3;
  transform: rotate(-0.5deg);
}

/* Форма профиля */
.profile-page {
  margin-bottom: 6rem;
  padding: 0 0.5rem;
}

/* Приветственная страница */
.profile-welcome {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.welcome-note {
  position: relative;
  background: #fff3cd;
  padding: 3rem 2rem;
  border-radius: 8px;
  transform: rotate(-1deg);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 400px;
}

.dark-theme .welcome-note {
  background: #4a4a00;
  color: #ffeb3b;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.welcome-content {
  position: relative;
  z-index: 2;
}

.welcome-title {
  font-family: 'Caveat', cursive;
  font-size: 2rem;
  font-weight: 700;
  color: #2d2d2d;
  margin-bottom: 1rem;
  transform: rotate(1deg);
}

.dark-theme .welcome-title {
  color: #ffeb3b;
}

.welcome-text {
  font-size: 1rem;
  line-height: 1.5;
  color: #666;
  margin-bottom: 2rem;
}

.dark-theme .welcome-text {
  color: #e6d700;
}

.create-profile-button {
  background: #2d2d2d;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-family: 'Kalam', cursive;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transform: rotate(-1deg);
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.dark-theme .create-profile-button {
  background: #ffeb3b;
  color: #1a1a1a;
}

.create-profile-button:hover {
  transform: rotate(0deg) scale(1.05);
  background: #1a1a1a;
}

.dark-theme .create-profile-button:hover {
  background: #fff176;
}

.welcome-decorations {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
}

.decoration-star,
.decoration-heart {
  width: 20px;
  height: 20px;
  color: rgba(255, 193, 7, 0.3);
}

.dark-theme .decoration-star,
.dark-theme .decoration-heart {
  color: rgba(255, 235, 59, 0.3);
}

/* Форма профиля */
.profile-form-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transform: rotate(-0.5deg);
}

.dark-theme .profile-form-container {
  background: #2d2d2d;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.form-header {
  background: #f8f8f8;
  padding: 1.5rem 2rem;
  border-bottom: 2px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dark-theme .form-header {
  background: #1a1a1a;
  border-bottom-color: #555;
}

.form-title {
  font-family: 'Caveat', cursive;
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d2d2d;
  transform: rotate(1deg);
}

.dark-theme .form-title {
  color: #e0e0e0;
}

.cancel-button {
  background: none;
  border: none;
  font-size: 2rem;
  color: #999;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.cancel-button:hover {
  background: #f0f0f0;
  color: #666;
}

.dark-theme .cancel-button:hover {
  background: #555;
  color: #ccc;
}

.profile-form {
  padding: 2rem;
  max-height: 70vh;
  overflow-y: auto;
}

.form-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #fafafa;
  border-radius: 8px;
  border: 2px dashed #ddd;
  transform: rotate(0.5deg);
}

.dark-theme .form-section {
  background: #1a1a1a;
  border-color: #555;
}

.form-section:nth-child(even) {
  transform: rotate(-0.5deg);
}

.section-title-form {
  font-family: 'Caveat', cursive;
  font-size: 1.4rem;
  font-weight: 600;
  color: #2d2d2d;
  margin-bottom: 1rem;
  transform: rotate(-1deg);
}

.dark-theme .section-title-form {
  color: #e0e0e0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-field {
  margin-bottom: 1rem;
}

.field-label {
  display: block;
  font-family: 'Caveat', cursive;
  font-size: 1.1rem;
  font-weight: 500;
  color: #666;
  margin-bottom: 0.5rem;
  transform: rotate(-0.5deg);
}

.dark-theme .field-label {
  color: #999;
}

.field-input,
.field-select {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 6px;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  background: white;
  color: #2d2d2d;
  transition: all 0.3s ease;
}

.dark-theme .field-input,
.dark-theme .field-select {
  background: #1a1a1a;
  border-color: #555;
  color: #e0e0e0;
}

.field-input:focus,
.field-select:focus {
  outline: none;
  border-color: #4caf50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

.field-input::placeholder {
  color: #999;
  font-style: italic;
}

.dark-theme .field-input::placeholder {
  color: #666;
}

.checkbox-field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.field-checkbox {
  width: auto;
  margin: 0;
}

.checkbox-label {
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  color: #666;
  cursor: pointer;
}

.dark-theme .checkbox-label {
  color: #999;
}

.textarea-container {
  position: relative;
}

.field-textarea {
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 2px solid #ddd;
  border-radius: 6px;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  background: white;
  color: #2d2d2d;
  resize: vertical;
  transition: all 0.3s ease;
}

.dark-theme .field-textarea {
  background: #1a1a1a;
  border-color: #555;
  color: #e0e0e0;
}

.field-textarea:focus {
  outline: none;
  border-color: #4caf50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

.field-textarea::placeholder {
  color: #999;
  font-style: italic;
}

.dark-theme .field-textarea::placeholder {
  color: #666;
}

.char-counter {
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 0.75rem;
  color: #999;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 6px;
  border-radius: 4px;
}

.dark-theme .char-counter {
  background: rgba(26, 26, 26, 0.9);
  color: #666;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 2px dashed #ddd;
}

.dark-theme .form-actions {
  border-top-color: #555;
}

.cancel-form-button {
  background: #f8f8f8;
  border: 2px solid #ddd;
  color: #666;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: rotate(1deg);
}

.dark-theme .cancel-form-button {
  background: #1a1a1a;
  border-color: #555;
  color: #999;
}

.cancel-form-button:hover {
  transform: rotate(0deg);
  background: #f0f0f0;
  color: #2d2d2d;
}

.dark-theme .cancel-form-button:hover {
  background: #333;
  color: #e0e0e0;
}

.save-profile-button {
  background: #4caf50;
  border: none;
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: rotate(-1deg);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.save-profile-button:hover {
  transform: rotate(0deg) scale(1.05);
  background: #45a049;
}

/* Отображение профиля */
.profile-display {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transform: rotate(-0.3deg);
}

.dark-theme .profile-display {
  background: #2d2d2d;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.profile-header {
  background: linear-gradient(135deg, #4caf50, #8bc34a);
  padding: 2rem;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.profile-name {
  font-family: 'Caveat', cursive;
  font-size: 2.2rem;
  font-weight: 700;
  transform: rotate(1deg);
}

.edit-profile-button {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  transform: rotate(-1deg);
}

.edit-profile-button:hover {
  transform: rotate(0deg);
  background: rgba(255, 255, 255, 0.3);
}

.edit-profile-button svg {
  width: 16px;
  height: 16px;
}

.profile-content {
  padding: 2rem;
}

.profile-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #fafafa;
  border-radius: 8px;
  border-left: 4px solid #4caf50;
  transform: rotate(0.3deg);
}

.dark-theme .profile-section {
  background: #1a1a1a;
}

.profile-section:nth-child(even) {
  transform: rotate(-0.3deg);
}

.profile-section-title {
  font-family: 'Caveat', cursive;
  font-size: 1.4rem;
  font-weight: 600;
  color: #2d2d2d;
  margin-bottom: 1rem;
  transform: rotate(-0.5deg);
}

.dark-theme .profile-section-title {
  color: #e0e0e0;
}

.profile-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.profile-info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  font-size: 0.8rem;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-family: 'Kalam', cursive;
  font-size: 1rem;
  color: #2d2d2d;
  font-weight: 500;
}

.dark-theme .info-value {
  color: #e0e0e0;
}

.info-link {
  font-family: 'Kalam', cursive;
  font-size: 1rem;
  color: #4caf50;
  text-decoration: none;
  font-weight: 500;
}

.info-link:hover {
  text-decoration: underline;
}

.profile-contacts {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.contact-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  text-decoration: none;
  font-family: 'Kalam', cursive;
  font-weight: 500;
  transition: all 0.3s ease;
  transform: rotate(-1deg);
}

.contact-link:hover {
  transform: rotate(0deg) scale(1.05);
}

.contact-link.telegram {
  background: #e3f2fd;
  color: #1976d2;
}

.dark-theme .contact-link.telegram {
  background: #0d47a1;
  color: #bbdefb;
}

.contact-link.instagram {
  background: #fce4ec;
  color: #c2185b;
}

.dark-theme .contact-link.instagram {
  background: #880e4f;
  color: #f8bbd9;
}

.contact-icon {
  font-size: 1.2rem;
}

.profile-text-block {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: white;
  border-radius: 6px;
  border: 2px dashed #ddd;
  transform: rotate(-0.2deg);
}

.dark-theme .profile-text-block {
  background: #333;
  border-color: #555;
}

.profile-text-block:nth-child(even) {
  transform: rotate(0.2deg);
}

.text-block-title {
  font-family: 'Caveat', cursive;
  font-size: 1.2rem;
  font-weight: 600;
  color: #4caf50;
  margin-bottom: 0.5rem;
  transform: rotate(-0.5deg);
}

.text-block-content {
  font-size: 0.95rem;
  line-height: 1.5;
  color: #666;
}

.dark-theme .text-block-content {
  color: #ccc;
}

/* Адаптивность для мобильных */
@media (max-width: 640px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .profile-form {
    padding: 1rem;
  }

  .form-header {
    padding: 1rem;
  }

  .profile-header {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .profile-content {
    padding: 1rem;
  }

  .profile-info-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column;
  }

  .welcome-note {
    padding: 2rem 1rem;
    margin: 0 1rem;
  }
}

/* Стили для мэтчинга */
.matching-page {
  margin-bottom: 6rem;
  padding: 0 0.5rem;
}

/* Приветственная страница мэтчинга */
.matching-welcome {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.matching-intro-note {
  position: relative;
  background: #e8f5e8;
  padding: 3rem 2rem;
  border-radius: 12px;
  transform: rotate(-1deg);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 400px;
}

.dark-theme .matching-intro-note {
  background: #1b5e20;
  color: #c8e6c9;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.matching-intro-content {
  position: relative;
  z-index: 2;
}

.matching-intro-title {
  font-family: 'Caveat', cursive;
  font-size: 2rem;
  font-weight: 700;
  color: #2e7d32;
  margin-bottom: 1rem;
  transform: rotate(1deg);
}

.dark-theme .matching-intro-title {
  color: #c8e6c9;
}

.matching-description {
  margin-bottom: 2rem;
}

.matching-text {
  font-size: 1.1rem;
  line-height: 1.5;
  color: #2e7d32;
  margin-bottom: 1.5rem;
  font-weight: 500;
}

.dark-theme .matching-text {
  color: #a5d6a7;
}

.matching-features {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #388e3c;
}

.dark-theme .feature-item {
  color: #81c784;
}

.feature-icon {
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
}

.feature-text {
  font-family: 'Kalam', cursive;
}

.matching-requirement {
  font-size: 0.9rem;
  color: #666;
  font-style: italic;
  margin-bottom: 0;
}

.dark-theme .matching-requirement {
  color: #a5d6a7;
}

.create-profile-for-matching {
  background: #4caf50;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-family: 'Kalam', cursive;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transform: rotate(-1deg);
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.create-profile-for-matching:hover {
  transform: rotate(0deg) scale(1.05);
  background: #45a049;
}

.matching-decorations {
  position: absolute;
  top: 1rem;
  right: 1rem;
}

.decoration-hearts {
  width: 60px;
  height: 30px;
  color: rgba(76, 175, 80, 0.3);
}

.dark-theme .decoration-hearts {
  color: rgba(129, 199, 132, 0.3);
}

/* Контейнер карточек */
.matching-cards-container {
  max-width: 400px;
  margin: 0 auto;
}

.matching-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 0 1rem;
}

.matching-title {
  font-family: 'Caveat', cursive;
  font-size: 2rem;
  font-weight: 700;
  color: #2d2d2d;
  transform: rotate(-1deg);
}

.dark-theme .matching-title {
  color: #e0e0e0;
}

.matching-counter {
  background: #4caf50;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  font-weight: 600;
  transform: rotate(2deg);
}

/* Стек карточек */
.cards-stack {
  position: relative;
  margin-bottom: 2rem;
  display: grid;
  gap: 20px;
}

.matching-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  user-select: none;
  height: 600px;
}

.dark-theme .matching-card {
  background: #2d2d2d;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.matching-card:active {
  cursor: grabbing;
}

.matching-card.card-top {
  z-index: 10;
}

.matching-card.card-middle {
  z-index: 9;
  transform: scale(0.95) translateY(10px);
}

.matching-card.card-bottom {
  z-index: 8;
  transform: scale(0.9) translateY(20px);
}

/* Индикатор свайпа */
.swipe-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.swipe-indicator.left,
.swipe-indicator.right {
  opacity: 1;
}

.swipe-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 12px;
  font-family: 'Caveat', cursive;
  font-weight: 700;
  font-size: 1.2rem;
}

.swipe-icon.like {
  background: rgba(76, 175, 80, 0.9);
  color: white;
}

.swipe-icon.pass {
  background: rgba(244, 67, 54, 0.9);
  color: white;
}

.swipe-icon svg {
  width: 40px;
  height: 40px;
}

/* Содержимое карточки */
.card-content {
  padding: 2rem;
  height: 600px;
  overflow-y: auto;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px dashed #eee;
}

.dark-theme .card-header {
  border-bottom-color: #555;
}

.card-name {
  font-family: 'Caveat', cursive;
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d2d2d;
  transform: rotate(-1deg);
}

.dark-theme .card-name {
  color: #e0e0e0;
}

.card-city {
  background: #4caf50;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-family: 'Kalam', cursive;
  font-size: 0.9rem;
  font-weight: 600;
  transform: rotate(2deg);
}

.card-info {
  margin-bottom: 1.5rem;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem;
  background: #f8f8f8;
  border-radius: 8px;
  transform: rotate(-0.5deg);
}

.dark-theme .info-row {
  background: #1a1a1a;
}

.info-row:nth-child(even) {
  transform: rotate(0.5deg);
}

.info-icon {
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
}

.info-text {
  font-family: 'Kalam', cursive;
  font-size: 0.95rem;
  color: #2d2d2d;
}

.dark-theme .info-text {
  color: #e0e0e0;
}

.card-descriptions {
  margin-bottom: 1.5rem;
}

.description-block {
  margin-bottom: 1rem;
  padding: 1rem;
  background: #fff3cd;
  border-radius: 8px;
  border-left: 4px solid #ffc107;
  transform: rotate(-0.3deg);
}

.dark-theme .description-block {
  background: #4a4a00;
  border-left-color: #ffeb3b;
}

.description-block:nth-child(even) {
  transform: rotate(0.3deg);
  background: #e3f2fd;
  border-left-color: #2196f3;
}

.dark-theme .description-block:nth-child(even) {
  background: #0d47a1;
  border-left-color: #64b5f6;
}

.description-title {
  font-family: 'Caveat', cursive;
  font-size: 1.1rem;
  font-weight: 600;
  color: #856404;
  margin-bottom: 0.5rem;
  transform: rotate(-0.5deg);
}

.dark-theme .description-title {
  color: #ffeb3b;
}

.description-block:nth-child(even) .description-title {
  color: #1976d2;
}

.dark-theme .description-block:nth-child(even) .description-title {
  color: #64b5f6;
}

.description-text {
  font-size: 0.9rem;
  line-height: 1.4;
  color: #666;
}

.dark-theme .description-text {
  color: #ccc;
}

.card-contacts {
  background: #e8f5e8;
  padding: 1rem;
  border-radius: 8px;
  transform: rotate(0.5deg);
}

.dark-theme .card-contacts {
  background: #1b5e20;
}

.contacts-title {
  font-family: 'Caveat', cursive;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2e7d32;
  margin-bottom: 0.75rem;
  transform: rotate(-0.5deg);
}

.dark-theme .contacts-title {
  color: #c8e6c9;
}

.contacts-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.contact-icon {
  font-size: 1rem;
  width: 20px;
  text-align: center;
}

.contact-text {
  font-family: 'Kalam', cursive;
  color: #2e7d32;
}

.dark-theme .contact-text {
  color: #a5d6a7;
}

/* Сообщение об окончании карточек */
.no-more-cards {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
}

.no-cards-note {
  position: relative;
  background: #fff3cd;
  padding: 3rem 2rem;
  border-radius: 12px;
  transform: rotate(-2deg);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  text-align: center;
  margin: 0 1rem;
}

.dark-theme .no-cards-note {
  background: #4a4a00;
  color: #ffeb3b;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.no-cards-content {
  position: relative;
  z-index: 2;
}

.no-cards-title {
  font-family: 'Caveat', cursive;
  font-size: 2rem;
  font-weight: 700;
  color: #856404;
  margin-bottom: 1rem;
  transform: rotate(1deg);
}

.dark-theme .no-cards-title {
  color: #ffeb3b;
}

.no-cards-text {
  font-size: 1rem;
  color: #666;
  margin-bottom: 2rem;
}

.dark-theme .no-cards-text {
  color: #e6d700;
}

.reset-matching-button {
  background: #856404;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-family: 'Kalam', cursive;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transform: rotate(-1deg);
  transition: all 0.3s ease;
}

.dark-theme .reset-matching-button {
  background: #ffeb3b;
  color: #1a1a1a;
}

.reset-matching-button:hover {
  transform: rotate(0deg) scale(1.05);
  background: #6d5103;
}

.dark-theme .reset-matching-button:hover {
  background: #fff176;
}

/* Кнопки управления */
.matching-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 0 1rem;
}

.control-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: white;
  border: 2px solid #ddd;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Kalam', cursive;
  font-size: 0.8rem;
  color: #666;
}

.dark-theme .control-button {
  background: #2d2d2d;
  border-color: #555;
  color: #999;
}

.control-button:hover:not(:disabled) {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.control-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.control-button.previous {
  border-color: #999;
}

.control-button.previous svg {
  width: 20px;
  height: 20px;
}

.control-button.pass {
  border-color: #f44336;
  color: #f44336;
}

.control-button.pass:hover:not(:disabled) {
  background: #f44336;
  color: white;
}

.control-button.like {
  border-color: #4caf50;
  color: #4caf50;
}

.control-button.like:hover:not(:disabled) {
  background: #4caf50;
  color: white;
}

.control-button svg {
  width: 24px;
  height: 24px;
}

.control-button span {
  font-size: 0.7rem;
  text-align: center;
  line-height: 1;
}

/* Адаптивность для мобильных */
@media (max-width: 640px) {
  .matching-features {
    grid-template-columns: 1fr;
  }

  .matching-intro-note {
    padding: 2rem 1rem;
    margin: 0 1rem;
  }

  .cards-stack {
    height: 500px;
  }

  .card-content {
    padding: 1.5rem;
    height: 500px;
  }

  .matching-controls {
    gap: 0.5rem;
  }

  .control-button {
    width: 50px;
    height: 50px;
  }

  .control-button.previous {
    width: 40px;
    height: 40px;
  }
}

.profile-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-modal {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 400px;
  width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 2rem 1.5rem 1.5rem 1.5rem;
}

.dark-theme .profile-modal {
  background: #2d2d2d;
}

.profile-modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 2rem;
  color: #999;
  cursor: pointer;
  z-index: 10;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  transition: background 0.2s;
}

.profile-modal-close:hover {
  background: #f0f0f0;
  color: #2d2d2d;
}

.dark-theme .profile-modal-close:hover {
  background: #555;
  color: #e0e0e0;
}


/* Стили для аватарки и оверлея */
.avatar-container {
  position: relative;
  width: 100%;
  max-width: 200px;
  margin: 0 auto 1rem;
}

.match-card-avatar {
  width: 100%;
  aspect-ratio: 1/1;
  object-fit: cover;
  border-radius: 12px;
  border: 2px solid #ddd;
  transition: all 0.3s ease;
}

.dark-theme .match-card-avatar {
  border-color: #555;
}

.avatar-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
}

.avatar-name {
  font-family: 'Caveat', cursive;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.avatar-age {
  font-family: 'Kalam', cursive;
  font-size: 1rem;
  font-weight: 500;
}

/* Стили для остального контента */
.match-card-header {
  background: #f8f8f8;
  padding: 1.5rem;
  border-bottom: 2px dashed #eee;
  text-align: center;
}

.dark-theme .match-card-header {
  background: #1a1a1a;
  border-bottom-color: #555;
}

.match-card-title {
  font-family: 'Caveat', cursive;
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d2d2d;
  transform: rotate(-1deg);
}

.dark-theme .match-card-title {
  color: #e0e0e0;
}

.match-card-content {
  padding: 1.5rem;
}

.match-card-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.match-card-info-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.match-card-city,
.match-card-role,
.match-card-company,
.match-card-about {
  font-family: 'Kalam', cursive;
  color: #666;
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
  padding: 0.5rem;
  background: #f8f8f8;
  border-radius: 6px;
  transform: rotate(-0.3deg);
  width: 100%;
  max-width: 300px;
}

.dark-theme .match-card-city,
.dark-theme .match-card-role,
.dark-theme .match-card-company,
.dark-theme .match-card-about {
  color: #999;
  background: #1a1a1a;
}

.match-card-city {
  background: #e8f5e8;
  color: #2e7d32;
}

.dark-theme .match-card-city {
  background: #1b5e20;
  color: #c8e6c9;
}

.match-card-role {
  transform: rotate(0.3deg);
}

.match-card-about {
  line-height: 1.4;
}

.match-card-button {
  background: #4caf50;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-family: 'Kalam', cursive;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transform: rotate(-1deg);
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.match-card-button:hover {
  transform: rotate(0deg);
  background: #45a049;
}

.dark-theme .match-card-button {
  background: #c8e6c9;
  color: #2e7d32;
}

.dark-theme .match-card-button:hover {
  background: #a5d6a7;
}

/* Адаптивность */
@media (max-width: 640px) {
  .avatar-container {
    max-width: 150px;
  }

  .avatar-name {
    font-size: 1.2rem;
  }

  .avatar-age {
    font-size: 0.9rem;
  }

  .match-card-title {
    font-size: 1.5rem;
  }

  .match-card-city,
  .match-card-role,
  .match-card-company,
  .match-card-about {
    font-size: 0.9rem;
    padding: 0.5rem;
  }
}
</style>